/**
 * FathomVoyage — Route Cost Calculator
 *
 * ⚠ ARCHITECTURE NOTE:
 * Pure function — no side effects, no imports from React.
 * When Cloudflare Workers are wired up (Phase 3), this entire file
 * moves to /workers/calculate-route.js as a Worker endpoint.
 * RoutesModule.jsx will call fetch('/api/calculate-route') instead
 * of importing directly — zero UI changes required.
 */

import {
  ROUTE_CORRIDORS, VESSEL_TYPES, ENROUTE_PRICES,
  WAR_RISK_ZONES, ROUTE_MATRIX,
} from './mockData.js';

// ─── Constants ────────────────────────────────────────────────────────────────
const PORT_COST_PER_CALL  = 15_000;  // $ per port call — load + discharge
const SUEZ_TRANSIT_DAYS   = 1.5;
const PANAMA_TRANSIT_DAYS = 1.0;

// CO₂ emission factors (tCO₂ per MT of fuel burned) — IMO standard values
export const EMISSION_FACTORS = {
  VLSFO: 3.114,
  HSFO:  3.206,
  MGO:   3.206,
  LNG:   2.750,
};

// CII risk thresholds — relative CO₂ delta between routes
// These are simplified indicative thresholds, not exact IMO CII band calculations
// Full CII calculation requires annual trading data — out of scope here
const CII_DELTA_THRESHOLDS = {
  A: { amber: 1500, red: 3000 },  // A-rated vessel can absorb more extra CO₂
  B: { amber: 1200, red: 2500 },
  C: { amber:  800, red: 1500 },  // C-rated vessel is already borderline
  D: { amber:  500, red: 1000 },
  E: { amber:  300, red:  600 },
};

/**
 * Main calculation entry point.
 *
 * @param {Object} params
 * @param {string} params.fromRegion       — Origin region ID
 * @param {string} params.toRegion         — Destination region ID
 * @param {string} params.vesselTypeId     — Vessel type key
 * @param {string} params.fuelType         — 'VLSFO' | 'HSFO' | 'MGO' | 'LNG'
 * @param {string} params.voyageType       — 'laden' | 'ballast'
 * @param {string} params.ciiRating        — 'A' | 'B' | 'C' | 'D' | 'E'
 * @param {number} params.tcRateOverride   — Optional $/day override
 * @param {number} params.speedOverride    — Optional knots override
 *
 * @returns {{ routes, cheapestId, summary, hasRoutes }}
 */
export function calculateRouteCosts({
  fromRegion,
  toRegion,
  vesselTypeId,
  fuelType    = 'VLSFO',
  voyageType  = 'laden',
  ciiRating   = 'B',
  tcRateOverride  = null,
  speedOverride   = null,
}) {
  // Guard: unknown vessel
  const vessel = VESSEL_TYPES[vesselTypeId];
  if (!vessel) return { routes: [], cheapestId: null, summary: '', hasRoutes: false };

  // Guard: no matrix entry for this OD pair
  const matrixKey   = `${fromRegion}-${toRegion}`;
  const corridorIds = ROUTE_MATRIX[matrixKey] || [];
  if (corridorIds.length === 0) {
    return { routes: [], cheapestId: null, summary: '', hasRoutes: false };
  }

  const tcRate = tcRateOverride ?? vessel.tcRate;
  const speed  = speedOverride  ?? vessel.speed[voyageType] ?? vessel.speed.laden;

  const routes = corridorIds
    .map(id => {
      try {
        return calcSingleRoute({ corridorId: id, vessel, vesselTypeId, fuelType, voyageType, ciiRating, tcRate, speed });
      } catch (e) {
        console.error(`[Calculator] Failed to calculate route ${id}:`, e);
        return null;
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.totalCost - b.totalCost);

  if (!routes.length) return { routes: [], cheapestId: null, summary: '', hasRoutes: false };

  const cheapestId = routes[0].corridorId;

  // CII: compute relative CO₂ delta against cheapest route for amber/red flagging
  const minCO2 = routes[0].co2Tonnes;
  routes.forEach(r => {
    r.co2Delta       = Math.round(r.co2Tonnes - minCO2);
    r.ciiTrafficLight = getCiiTrafficLight(r.co2Delta, r.corridorId === cheapestId, ciiRating);
  });

  return {
    routes,
    cheapestId,
    summary:   buildSummary(routes, ciiRating),
    hasRoutes: true,
  };
}

// ─── Single route ─────────────────────────────────────────────────────────────
function calcSingleRoute({ corridorId, vessel, vesselTypeId, fuelType, voyageType, ciiRating, tcRate, speed }) {
  const corridor = ROUTE_CORRIDORS[corridorId];
  if (!corridor) throw new Error(`Unknown corridor: ${corridorId}`);

  const distance = corridor.distance || 0;

  // ── Voyage days ──────────────────────────────────────────────────────────
  const speedKts   = Math.max(speed, 6);  // guard against 0
  const seaDays    = distance / (speedKts * 24);
  const canalDays  = (corridor.canals.includes('suez')   ? SUEZ_TRANSIT_DAYS   : 0)
                   + (corridor.canals.includes('panama') ? PANAMA_TRANSIT_DAYS : 0);
  const totalDays  = seaDays + canalDays;

  // ── Fuel ─────────────────────────────────────────────────────────────────
  const consumption  = vessel.consumption?.[voyageType] ?? vessel.consumption?.laden ?? 50;
  const fuelConsumed = seaDays * consumption;

  // ── Bunker cost ───────────────────────────────────────────────────────────
  const portData   = ENROUTE_PRICES[corridor.fuelPort] || ENROUTE_PRICES['SGP'] || {};
  const bunkerPrice = portData[fuelType] ?? portData['VLSFO'] ?? 720;
  const bunkerCost  = fuelConsumed * bunkerPrice;

  // ── Canal fees ────────────────────────────────────────────────────────────
  const canalFees = corridor.canals.reduce((total, canal) => {
    const fee = vessel.canalFees?.[canal];
    return total + (fee ?? 0);
  }, 0);

  // ── War risk ──────────────────────────────────────────────────────────────
  const warRiskBreakdown = (corridor.warRiskZones || []).map(zoneId => {
    const zone    = WAR_RISK_ZONES[zoneId] || {};
    const premium = zone.premium ?? 0;
    const cost    = (vessel.hullValue ?? 0) * premium / 100;
    return { zone: zone.label || zoneId, premium, cost, color: zone.color || '#94A3B8' };
  });
  const warRiskCost = warRiskBreakdown.reduce((s, w) => s + w.cost, 0);

  // ── TC cost ───────────────────────────────────────────────────────────────
  const tcCost = totalDays * (tcRate || 0);

  // ── Port costs ────────────────────────────────────────────────────────────
  const portCosts = PORT_COST_PER_CALL * 2;

  // ── CO₂ ───────────────────────────────────────────────────────────────────
  const emissionFactor = EMISSION_FACTORS[fuelType] ?? EMISSION_FACTORS.VLSFO;
  const co2Tonnes      = Math.round(fuelConsumed * emissionFactor);

  // ── Total ─────────────────────────────────────────────────────────────────
  const totalCost = bunkerCost + canalFees + warRiskCost + tcCost + portCosts;

  return {
    corridorId,
    corridor,
    label:    corridor.label,
    color:    corridor.color,

    distanceNM:   Math.round(distance),
    seaDays:      Math.round(seaDays * 10) / 10,
    canalDays,
    totalDays:    Math.round(totalDays * 10) / 10,
    fuelConsumed: Math.round(fuelConsumed),
    speed:        speedKts,
    voyageType,

    bunkerCost:  Math.round(bunkerCost),
    canalFees:   Math.round(canalFees),
    warRiskCost: Math.round(warRiskCost),
    tcCost:      Math.round(tcCost),
    portCosts:   Math.round(portCosts),
    totalCost:   Math.round(totalCost),

    bunkerPort:        portData,
    bunkerPrice,
    fuelType,
    warRiskBreakdown,
    canals:            corridor.canals || [],
    warRiskZones:      corridor.warRiskZones || [],
    notes:             corridor.notes || '',
    fuelPortId:        corridor.fuelPort,
    waypoints:         corridor.waypoints || [],

    co2Tonnes,
    co2Delta:        0,   // set after all routes calculated
    ciiTrafficLight: null,
  };
}

// ─── CII traffic light ────────────────────────────────────────────────────────
function getCiiTrafficLight(co2Delta, isCheapest, ciiRating) {
  if (isCheapest) {
    return {
      light:   'green',
      icon:    '🟢',
      message: 'Lowest CO₂ option on this route',
    };
  }

  const thresholds = CII_DELTA_THRESHOLDS[ciiRating] || CII_DELTA_THRESHOLDS['B'];

  if (co2Delta >= thresholds.red) {
    return {
      light:   'red',
      icon:    '🔴',
      message: `+${co2Delta.toLocaleString()} tonnes CO₂ vs cheapest route — significant CII impact, especially if currently rated ${ciiRating}`,
    };
  }
  if (co2Delta >= thresholds.amber) {
    return {
      light:   'amber',
      icon:    '🟡',
      message: `+${co2Delta.toLocaleString()} tonnes CO₂ vs cheapest route — monitor CII impact if vessel is rated ${ciiRating} or below`,
    };
  }
  return {
    light:   'green',
    icon:    '🟢',
    message: `+${co2Delta.toLocaleString()} tonnes CO₂ vs cheapest route — low CII impact`,
  };
}

// ─── Insight summary ──────────────────────────────────────────────────────────
function buildSummary(routes, ciiRating) {
  if (routes.length < 2) return routes[0]?.notes || '';

  const best   = routes[0];
  const worst  = routes[routes.length - 1];
  const saving = worst.totalCost - best.totalCost;

  const bestIsLonger  = best.totalDays > worst.totalDays;
  const dayDiff       = Math.abs(best.totalDays - worst.totalDays).toFixed(1);
  const co2Extra      = best.co2Delta > 0 ? best.co2Tonnes - Math.min(...routes.map(r => r.co2Tonnes)) : worst.co2Tonnes - best.co2Tonnes;

  let summary = `${best.label} saves ${fmtM(saving)} vs ${worst.label}`;

  if (bestIsLonger) {
    const premiumTotal = (worst.warRiskCost || 0) + (worst.canalFees || 0);
    summary += ` — despite adding ${dayDiff} days at sea`;
    if (premiumTotal > 0) {
      const parts = [];
      if (worst.warRiskCost > 0) parts.push(`war risk (${fmtM(worst.warRiskCost)})`);
      if (worst.canalFees > 0)   parts.push(`canal fees (${fmtM(worst.canalFees)})`);
      summary += ` — because ${parts.join(' + ')} on ${worst.label} total ${fmtM(premiumTotal)}`;
    }
  } else {
    summary += ` and arrives ${dayDiff} days sooner`;
  }

  if (co2Extra > 500) {
    summary += `. Note: ${worst.label} emits ${co2Extra.toLocaleString()} more tonnes CO₂${ciiRating === 'C' || ciiRating === 'D' || ciiRating === 'E' ? ' — consider CII impact given current rating' : ''}`;
  }

  return summary + '.';
}

export const fmtM = (n) => {
  if (!n && n !== 0) return '—';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n)}`;
};

// ─── Worker client functions ───────────────────────────────────────────────────

const ROUTES_BASE = () =>
  import.meta.env.VITE_ROUTES_WORKER_URL ||
  'https://fathomvoyage-routes.a-jaiswal78.workers.dev';

const BUNKER_BASE = () =>
  (import.meta.env.VITE_BUNKER_WORKER_URL || '').replace(/\/$/, '');

export async function fetchRouteCosts(params) {
  const {
    fromHub, toHub, fuelType,
    speedKts, consumptionLaden, bunkerPrice,
    tcRate, hmValue, canalDues, portCosts,
    awrpApplicableVariants, awrpDays, awrpRatePct,
  } = params;

  const response = await fetch(`${ROUTES_BASE()}/api/routes/calculate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      from_hub:          fromHub,
      to_hub:            toHub,
      fuel_type:         fuelType,
      speed_kts:         Number(speedKts),
      consumption_laden: Number(consumptionLaden),
      bunker_price:      Number(bunkerPrice),
      tc_rate:           Number(tcRate),
      hm_value:          Number(hmValue),
      canal_dues:        Number(canalDues),
      port_costs:        Number(portCosts),
      awrp_applicable_variants: awrpApplicableVariants,
      awrp_days:                Number(awrpDays),
      awrp_rate_pct:            Number(awrpRatePct),
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Route calculation failed');
  }

  return response.json();
}

export async function fetchHubs() {
  const response = await fetch(`${ROUTES_BASE()}/api/routes/hubs`);
  return response.json();
}

/**
 * Fetches Global 20 VLSFO and IFO380 averages from the Bunker Worker.
 * Returns null fields on error — caller must handle gracefully.
 */
export async function fetchBunkerGlobal20() {
  const base = BUNKER_BASE();
  if (!base) return { vlsfo: null, ifo380: null, updatedAt: null, error: true };
  try {
    const res = await fetch(`${base}/api/bunker-prices`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const g20  = data.averages?.global_20;
    if (!g20) throw new Error('global_20 not in response');
    return {
      vlsfo:     g20.vlsfo   ?? null,
      ifo380:    g20.ifo380  ?? null,
      updatedAt: data.updated_at ?? null,
      error:     false,
    };
  } catch {
    return { vlsfo: null, ifo380: null, updatedAt: null, error: true };
  }
}
